/**
 * Decides whether the tasklist may save the card on top as its scroll anchor.
 *
 * After a jump — restore on load, "back to today", list/column toggle — the card the jump left on
 * top is the "rest card". Saving resumes only when BOTH hold: the user has given real input since
 * the jump (wheel, pointer, touch or a navigation key), AND the top card is no longer the rest card.
 * From then on saving stays allowed until the next jump.
 *
 * Why both: scroll events after a jump are not proof that the user moved the list (measured
 * 2026-10-01: "today" was overwritten 180–350 ms after "back to today" by such events), and input
 * alone is not proof either (a wheel at the list end, or a click on a card, moves nothing — the top
 * card is then still the rest card, and saving it would overwrite "today" or an anchor that could not
 * be scrolled to the top). The view's own compensation and reloads are not jumps: they keep the
 * visible card in place and must not block saving in the middle of a gesture.
 */
export class TasklistSaveGate {

    private _afterJump = false;

    private _restUid: string | null = null;

    private _inputSinceJump = false;

    /** A jump left `restUid` on top (null when no card is on top). Blocks saving. */
    public jumped(restUid: string | null): void {
        this._afterJump = true;
        this._restUid = restUid;
        this._inputSinceJump = false;
    }

    /** The user gave real input (wheel, pointer, touch, navigation key). */
    public userInput(): void {
        this._inputSinceJump = true;
    }

    /** Whether `topUid`, the card now on top, may be saved. */
    public allows(topUid: string): boolean {
        if (!this._afterJump) {
            return true;
        }

        if (!this._inputSinceJump || topUid === this._restUid) {
            return false;
        }

        this._afterJump = false;
        return true;
    }
}
